<template>
  <div class="relative grid grid-cols-3 items-start gap-5 overflow-hidden" style="height: calc(100vh - 175px)">
    <!-- Loading state -->
    <div v-if="loadingLandingPages" class="absolute left-1/2 transform -translate-x-1/2">
      <BaseLoadingLogo />
    </div>
    <!-- Empty state -->
    <div v-if="!loadingLandingPages && landingPages.length === 0" style="top: 40%; width: 350px"
    class="absolute left-1/2 transform -translate-x-1/2 -translate-y-1/2 flex flex-col gap-2 items-center">
      <img src="../../../assets/images/nothing-here-yet.png" class="w-20 h-20">
      <BaseText type="label" size="sm" class="text-text-muted">
        Nothing here yet...
      </BaseText>
      <BaseText type="body" size="sm" class="text-text-normal text-center">
        Once your brand's active landing pages are scraped, their screenshots will appear here.
      </BaseText>
    </div>
    <!-- Landing pages -->
    <div v-if="!loadingLandingPages && landingPages.length > 0" style="height: calc(-175px + 100vh)"
    class="border border-border-normal col-span-1 rounded-2xl">
      <!-- Section Title -->
      <div class="flex justify-start gap-2 p-4">
        <img src="../../../assets/icons/link-document.svg" class="h-5">
        <BaseText type="label" size="sm" class="text-text-muted">
          Landing Pages
        </BaseText>
      </div>

      <!-- Landing Page List -->
      <div class="flex flex-col gap-2 px-2 pb-20 overflow-y-scroll h-full scrollbar-hide">
        <div v-for="(page, index) in landingPages" :key="page.landing_page" :ref="`landing-page-${index}`"
        class="flex flex-col gap-2 w-full p-2 rounded-md transition-all duration-200"
        :class="[{
          'bg-primary-red-10 border border-primary-red-100 text-primary-red-300': selectedUrl === page.landing_page,
          'hover:bg-background-normal border border-transparent text-text-muted': selectedUrl !== page.landing_page && !locked,
        }, locked ? 'cursor-default' : 'cursor-pointer']"
        @click="selectLandingPage(page)">
          <!-- Top Row -->
          <div class="flex items-center gap-2">
            <!-- Icon -->
            <LinkRoundedIcon class="transition-colors flex-shrink-0" :class="selectedUrl === page.landing_page ? 'text-primary-red-300' : 'text-icon-normal'" />
            <!-- Landing Page Text -->
            <BaseText type="body" size="sm" class="whitespace-nowrap overflow-ellipsis overflow-hidden">
              {{ page.landing_page }}
            </BaseText>
          </div>
          <!-- Bottom Row -->
          <div class="flex justify-start items-center gap-1 p-0.5" >
            <!-- Landing Page Count Active -->
            <div class="flex justify-start">
              <div class="flex items-center justify-center p-1.5">
                <div class="w-1 h-1 rounded-full" :class="selectedUrl === page.landing_page ? 'bg-primary-red-300' : 'bg-secondary-green-100'" />
              </div>
              <BaseText type="label" size="xs" class="text-text-muted"
              :class="selectedUrl === page.landing_page ? 'text-primary-red-300' : 'text-text-muted'">
                {{ formatCount(page.active_count || 0) }} Active
              </BaseText>
            </div>

            <!-- Landing Page Count Inactive -->
            <div class="flex justify-start">
              <!-- Indicator Dot -->
              <div class="flex items-center justify-center p-1.5">
                <div class="w-1 h-1 rounded-full" :class="selectedUrl === page.landing_page ? 'bg-primary-red-300 opacity-60' : 'bg-text-normal'" />
              </div>
              <BaseText type="label" size="xs" :class="selectedUrl === page.landing_page ? 'text-primary-red-300 opacity-60' : 'text-text-normal'">
                {{ formatCount(page.inactive_count || 0) }} Inactive
              </BaseText>
            </div>
          </div>
        </div>
        <BaseText v-if="locked" type="body" size="sm" class="w-full text-center text-text-disabled mt-2.5">
          Track this brand to see more
        </BaseText>
        <infinite-loading
          v-if="landingPages.length && !locked"
          :identifier="infiniteId"
          @infinite="getMoreLandingPages"
        >
          <div slot="spinner">
            <BaseLoadingLogo :margin="2" />
          </div>
          <div slot="no-more" />
          <div slot="no-results" />
        </infinite-loading>
      </div>
    </div>

    <!-- Screenshot Section -->
    <div v-if="!loadingLandingPages && landingPages.length > 0" style="height: calc(-175px + 100vh)"
    class="col-span-2 relative rounded-2xl w-full p-2 bg-background-normal" 
    :class="{'pointer-events-none overflow-hidden select-none': locked}">
      <PreviewLockedOverlay v-if="locked" class="absolute inset-0 z-10 rounded-2xl"
      :title="`Start Tracking ${brand?.name || 'this brand'} to Unlock Screenshots`"
      description="See landing page screenshots for both desktop and mobile devices."
      use-backdrop />
      <!-- Top Bar -->
      <div class="flex pb-2 bg-background-normal backdrop-blur-sm">
        <!-- Url Display -->
        <div class="flex justify-start rounded-lg gap-1.5 p-2.5 bg-white box-shadow mr-2" style="max-width: 50%">
          <LinkRoundedIcon class="text-icon-normal flex-shrink-0" />
          <BaseText type="body" size="sm" class="text-text-muted truncate">
            {{ locked ? landingPages[0].landing_page : selectedUrl }}
          </BaseText>
        </div>
        <!-- <div class="ml-3 flex bg-white p-2.5 rounded-lg box-shadow"> -->
        <transition>
          <button v-if="!urlCopied || copiedUrl !== selectedUrl" class="flex flex-row gap-1 items-center bg-neutral-50 rounded-lg p-2"
          @click="copyUrl(selectedUrl)">
            <LayersCopyIcon class="text-icon-normal" />
            <BaseText type="body" size="sm" class="text-text-muted pr-0.5">
              Copy URL
            </BaseText>
          </button>
          <div v-else-if="urlCopied && copiedUrl === selectedUrl" class="flex flex-row gap-1 items-center bg-icon-muted pl-2 pr-4 rounded-lg cursor-default">
            <div class="flex flex-row items-center text-xs text-white">
              <img src="../../../assets/icons/new-check.svg" class="w-5 h-5">
              <div>
                Copied!
              </div>
            </div>
          </div>
        </transition>
        <!-- Device Toggle -->
        <div class="ml-auto flex p-0.5 rounded-lg bg-neutral-50">
          <button class="switcher group flex items-center gap-1 py-2 pl-2 pr-3 rounded-md" :class="imageDisplay === 'Mobile' ? 'selected bg-white' : 'bg-transparent'"
          @click="imageDisplay = 'Mobile'">
            <MobileDeviceIcon class="text-icon-normal transition-colors duration-200" :class="{'text-icon-muted': imageDisplay === 'Mobile'}" />
            <BaseText type="label" size="sm" class="text-text-normal transition-colors duration-200"
            :class="{'text-text-muted': imageDisplay === 'Mobile'}">
              Mobile
            </BaseText>
          </button>
          <button class="switcher group flex items-center gap-1 py-2 pl-2 pr-3 rounded-md" :class="imageDisplay === 'Desktop' ? 'selected bg-white' : 'bg-transparent'"
          @click="imageDisplay = 'Desktop'">
            <DesktopDeviceIcon class="text-icon-normal transition-colors duration-200" :class="{'text-icon-muted': imageDisplay === 'Desktop'}" />
            <BaseText type="label" size="sm" class="text-text-normal transition-colors duration-200"
            :class="{'text-text-muted': imageDisplay === 'Desktop'}">
              Desktop
            </BaseText>
          </button>
        </div>
      </div>
      <!-- Image Section -->
      <div class="w-full relative flex items-center justify-center px-20 overflow-y-scroll pb-20" style="height: calc(-175px + 100vh)">
        <transition name="spinner">
          <div v-if="loadingImage" class="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 z-50 shadow-lg p-1.5 rounded-lg"
          style="background-color: rgba(0, 0, 0, 0.35); backdrop-filter: blur(36px);">
            <BaseLoadingSpinner />
          </div>
        </transition>
        <div class="absolute top-0 pb-20">
          <img v-if="locked" class="w-full max-w-md mt-5 rounded-2xl box-shadow"
          src="https://publicassets.foreplay.co/page-screenshot-placeholder.png">
          <img v-else-if="mostRecentLandingPageImage && imageDisplay === 'Desktop'" class="w-full max-w-md mt-5 rounded-2xl box-shadow"
          :src="mostRecentLandingPageImage" alt="Landing Page" @load="loadingImage = false">
          <img v-else class="w-full max-w-sm mt-5 rounded-2xl box-shadow"
          :src="mostRecentLandingPageImage" alt="Landing Page" @load="loadingImage = false">
        </div>
      </div>
      <!-- Bottom Bar -->
      <a :href="mostRecentLandingPageImage" target="_blank" download>
        <button class="group absolute bottom-2.5 left-2.5 rounded-md p-1.5 button-shadow bg-white">
          <DownloadIcon class="transition-colors text-icon-normal group-hover:text-icon-muted" />
        </button>
      </a>
    </div>
  </div>
</template>

<script>
import InfiniteLoading from 'vue-infinite-loading'
import ForeplayAPI from '@/api/foreplayServer'
import { normalizeLandingPagesMockData } from '@/utils/spyder/mockDataUtils'
import PreviewLockedOverlay from '../PreviewLockedOverlay.vue'

// Icons
import LinkRoundedIcon from '../../globals/Icons/LinkRoundedIcon.vue'
import LayersCopyIcon from '../../globals/Icons/LayersCopyIcon.vue'
import DownloadIcon from '../../globals/Icons/DownloadIcon.vue'
import MobileDeviceIcon from '../../globals/Icons/MobileDeviceIcon.vue'
import DesktopDeviceIcon from '../../globals/Icons/DesktopDeviceIcon.vue'

const LANDING_PAGES_PER_PAGE = 50

export default {
  name: 'LandingPagesBrandView',
  components: {
    PreviewLockedOverlay,
    InfiniteLoading,
    LinkRoundedIcon,
    LayersCopyIcon,
    DownloadIcon,
    MobileDeviceIcon,
    DesktopDeviceIcon
  },
  props: {
    brand: {
      type: Object,
      required: true
    },
    initialLandingPageUrl: {
      type: String,
      default: null
    }
  },
  data () {
    return {
      landingPages: [],
      selectedLandingPage: {},
      imageDisplay: 'Desktop',
      infiniteId: +new Date(),
      loadingLandingPages: true,
      loadingImage: false,
      urlCopied: false,
      copiedUrl: '',

      // Pagination
      currentPage: 1,
      allPagesLoaded: false
    }
  },
  watch: {
    brand: {
      handler (brand) {
        if (!brand?.id) return
        this.initLandingPages()
      },
      immediate: true
    }
  },
  computed: {
    locked () {
      const isSubscribed = this.brand?.is_subscribed
      return isSubscribed === undefined ? false : !isSubscribed
    },
    selectedUrl () {
      return this.selectedLandingPage?.landing_page ?? null
    },
    mostRecentLandingPageImage () {
      // Check which view to get
      if (this.imageDisplay === 'Mobile') {
        return this.selectedLandingPage.mobile_screenshot || null
      }

      return this.selectedLandingPage.desktop_screenshot || null
    }
  },
  methods: {
    async initLandingPages () {
      this.loadingLandingPages = true
      this.landingPages = []
      this.selectedLandingPage = {}
      this.currentPage = 1
      this.allPagesLoaded = false

      const pages = await this.fetchLandingPages(1)

      // Set the selected landing page, honouring a request to open a specific one
      let requestedIndex = -1
      if (pages.length) {
        requestedIndex = this.initialLandingPageUrl
          ? pages.findIndex(page => page.landing_page === this.initialLandingPageUrl)
          : -1

        this.selectedLandingPage = requestedIndex > -1 ? pages[requestedIndex] : pages[0]
        if (requestedIndex > -1) this.loadingImage = true
      }

      this.infiniteId += 1
      this.loadingLandingPages = false

      // The list is scrollable, so a preselected page can be out of view
      if (requestedIndex > -1) {
        this.$nextTick(() => { this.scrollLandingPageIntoView(requestedIndex) })
      }
    },
    // Fetches a page of landing pages, appending it to the list, and returns just that page's results
    async fetchLandingPages (page) {
      if (!this.brand?.id) return []
      if (!this.brand.is_subscribed) {
        this.landingPages = normalizeLandingPagesMockData(this.brand.analytics.landing_pages)
        this.allPagesLoaded = true
        return []
      }
      try {
        const response = await ForeplayAPI.Brands.getBrandLandingPages(
          this.brand.id,
          { limit: LANDING_PAGES_PER_PAGE, page }
        )
        const results = response?.data ?? []

        this.landingPages = page > 1 ? [...this.landingPages, ...results] : results
        this.currentPage = page
        this.allPagesLoaded = response?.pages ? page >= response.pages : results.length < LANDING_PAGES_PER_PAGE

        return results
      } catch (error) {
        console.error('Error fetching landing pages:', error)
        this.$showAlert({
          type: 'error',
          message: 'Failed to fetch landing pages. See console for details.'
        })
        this.allPagesLoaded = true
        return []
      }
    },
    selectLandingPage (page) {
      if (this.selectedUrl === page.landing_page || this.locked) return
      this.selectedLandingPage = page
      this.loadingImage = true
    },
    formatCount (value) {
      if (value === null || value === undefined) return ''
      try {
        // Use a locale that formats thousands with ',' (e.g., en-US → 12,345)
        return Number(value).toLocaleString('en-US')
      } catch (e) {
        return String(value)
      }
    },
    copyUrl (url) {
      navigator.clipboard.writeText(url)
      this.urlCopied = true
      this.copiedUrl = url
    },
    scrollLandingPageIntoView (index) {
      const element = this.$refs[`landing-page-${index}`]?.[0]
      if (!element) return
      element.scrollIntoView({ block: 'nearest' })
    },
    async getMoreLandingPages ($state) {
      if (this.allPagesLoaded || this.loadingLandingPages || !this.brand?.id) {
        $state?.complete()
        return
      }

      await this.fetchLandingPages(this.currentPage + 1)

      if (this.allPagesLoaded) $state?.complete()
      else $state?.loaded()
    }
  }
}
</script>

<style scoped>
.box-shadow {
  box-shadow: 0px 0.1px 0.25px 0px rgba(4, 26, 75, 0.02),
    0px 0.25px 0.5px 0px rgba(4, 26, 75, 0.06),
    0px 1px 2px 0px rgba(4, 26, 75, 0.12);

  box-shadow: 0px 0.1px 0.25px 0px color(display-p3 0.0368 0.0984 0.2832 / 0.02),
    0px 0.25px 0.5px 0px color(display-p3 0.0368 0.0984 0.2832 / 0.06),
    0px 1px 2px 0px color(display-p3 0.0368 0.0984 0.2832 / 0.12);
}
.switcher {
  box-shadow: none;
  transition: box-shadow 200ms ease-in-out, background-color 200ms ease-in-out;
}
.switcher.selected {
  box-shadow: 0px 0.1px 0.25px 0px rgba(4, 26, 75, 0.02), 0px 0.25px 0.5px 0px rgba(4, 26, 75, 0.06), 0px 1px 2px 0px rgba(4, 26, 75, 0.12);
}
.button-shadow {
  transition: box-shadow 300ms ease-in-out;
  box-shadow: 0px 1px 2px 0px rgba(4, 26, 75, 0.13), 0px 0px 0px 1px rgba(0, 56, 108, 0.08);
}
.button-shadow:hover {
  box-shadow: 0px 1px 2px 0px rgba(4, 26, 75, 0.13), 0px 0px 0px 1px rgba(0, 56, 108, 0.16);
}
.spinner-enter-active {
  transition: opacity 150ms ease-in-out;
  transition-delay: 200ms;
}
.spinner-leave-active {
  transition: opacity 150ms ease-in-out;
}
.spinner-enter-from, .spinner-enter, .spinner-leave-to {
  opacity: 0;
}
.spinner-enter-to, .spinner-leave-from {
  opacity: 1;
}
.v-enter-active {
  transition: opacity 150ms ease-in-out;
}
.v-enter-from, .v-enter, .v-leave-to {
  opacity: 0;
}
.v-enter-to, .v-leave-from {
  opacity: 1;
}
</style>
